import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

interface VirtualMeetingNotification {
  id: string;
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  meetingTitle: string;
  scheduledStartAt: string;
  eventType?: 'meeting_scheduled' | 'request_reviewed';
  message?: string;
}

const VirtualMeetingNotifications: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<VirtualMeetingNotification[]>([]);

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated || user?.role === 'admin') {
      setNotifications([]);
      return;
    }
    try {
      const response = await api.get('/virtual-meeting-notifications');
      setNotifications(Array.isArray(response.data?.data)
        ? response.data.data.filter((notification: VirtualMeetingNotification & { read?: boolean }) => !notification.read)
        : []);
    } catch (error: any) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        console.error('[virtual-meeting-notifications] refresh failed', error?.response?.status || error?.code || 'network error');
      }
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    void loadNotifications();
    if (!isAuthenticated || user?.role === 'admin') return undefined;
    const timer = window.setInterval(() => { void loadNotifications(); }, 60000);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, loadNotifications, user?.role]);

  const dismiss = async (notificationId: string) => {
    try {
      await api.patch(`/virtual-meeting-notifications/${notificationId}/read`);
      setNotifications((current) => current.filter((notification) => notification.id !== notificationId));
    } catch (error: any) {
      console.error('[virtual-meeting-notifications] dismiss failed', error?.response?.status || error?.code || 'network error');
    }
  };

  if (!notifications.length) return null;
  const latest = notifications[0];

  return (
    <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 0.75 }}>
      <Alert
        severity="info"
        variant="filled"
        sx={{
          py: 0,
          '& .MuiAlert-message': { width: '100%', minWidth: 0 },
        }}
        action={(
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void dismiss(latest.id);
                navigate(`/discussion-rooms?sessionRoom=${encodeURIComponent(latest.roomKey)}`);
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {latest.eventType === 'request_reviewed' ? 'View request' : 'View session'}
            </Button>
            <IconButton
              aria-label="Dismiss virtual session notification"
              color="inherit"
              size="small"
              onClick={() => void dismiss(latest.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {latest.eventType === 'request_reviewed'
            ? `Admin responded to your ${latest.examType} ${latest.subjectName} session request`
            : `New ${latest.examType} ${latest.subjectName} virtual session`}
        </Typography>
        <Typography variant="caption" component="span">
          {latest.message || `${latest.meetingTitle} · ${new Date(latest.scheduledStartAt).toLocaleString()}`}
          {notifications.length > 1 ? ` · ${notifications.length} unread` : ''}
        </Typography>
      </Alert>
    </Box>
  );
};

export default VirtualMeetingNotifications;
